import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard, Users as UsersIcon, Package, FileText, Truck, Globe2, Receipt,
  FileSignature, UserCog, LogOut, BarChart3, Wallet, Landmark, Clock,
  Building2, Zap, ShoppingCart, Settings as SettingsIcon, MessageSquareQuote, KeyRound, Briefcase, BadgeDollarSign, PiggyBank, BadgeCheck, CalendarDays,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth, hasModule } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { Toaster } from "@/components/ui/sonner";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";

const navItems = [
  { to: "/", key: "dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard", roles: ["admin", "sales", "warehouse"], module: "dashboard" },
  { to: "/clients", key: "clients", icon: UsersIcon, testid: "nav-clients", roles: ["admin", "sales", "warehouse"], module: "clients" },
  { to: "/attendance", key: "attendance", icon: Clock, testid: "nav-attendance", roles: ["admin", "sales"], module: "attendance" },
  { to: "/inventory", key: "general_inventory", icon: Package, testid: "nav-inventory", roles: ["admin", "sales", "warehouse"], module: "inventory" },
  { to: "/quotations", key: "quotations", icon: FileSignature, testid: "nav-quotations", roles: ["admin", "sales"], module: "quotations" },
  { to: "/orders", key: "orders", icon: FileText, testid: "nav-orders", roles: ["admin", "sales"], module: "orders" },
  { to: "/delivery-orders", key: "delivery_orders", icon: Truck, testid: "nav-delivery-orders", roles: ["admin", "sales", "warehouse"], module: "delivery_orders" },
  { to: "/invoices", key: "invoices", icon: Receipt, testid: "nav-invoices", roles: ["admin", "sales"], module: "invoices" },
  { to: "/payment-claims", key: "payment_claims", icon: BadgeDollarSign, testid: "nav-payment-claims", roles: ["admin", "sales", "finance"], module: "payment_claims", badge: "claims" },
  { to: "/suppliers", key: "suppliers", icon: Building2, testid: "nav-suppliers", roles: ["admin", "sales", "warehouse"], module: "suppliers" },
  { to: "/rfqs", key: "rfqs", icon: MessageSquareQuote, testid: "nav-rfqs", roles: ["admin", "sales", "warehouse"], module: "rfqs" },
  { to: "/purchase-orders", key: "purchase_orders", icon: ShoppingCart, testid: "nav-purchase-orders", roles: ["admin", "sales"], module: "purchase_orders" },
  { to: "/expenses", key: "expenses", icon: Wallet, testid: "nav-expenses", roles: ["admin", "sales"], module: "expenses" },
  { to: "/loans", key: "loans", icon: Landmark, testid: "nav-loans", roles: ["admin"], module: "loans" },
  { to: "/project-funds", key: "project_funds", icon: PiggyBank, testid: "nav-project-funds", roles: ["admin", "finance"], module: "project_funds" },
  { to: "/bills", key: "bills", icon: Zap, testid: "nav-bills", roles: ["admin", "finance"], module: "bills", badge: "bills" },
  { to: "/payroll", key: "payroll", icon: BadgeCheck, testid: "nav-payroll", roles: ["admin"], module: "payroll" },
  { to: "/leave", key: "leave", icon: CalendarDays, testid: "nav-leave", roles: ["admin", "sales", "warehouse", "it_support", "finance"], module: "leave", badge: "leave" },
  { to: "/licences", key: "software_licences", icon: KeyRound, testid: "nav-licences", roles: ["admin", "sales", "warehouse", "it_support", "finance"], module: "software_licences" },
  { to: "/assets", key: "company_assets", icon: Briefcase, testid: "nav-assets", roles: ["admin", "sales", "finance"], module: "company_assets" },
  { to: "/reports", key: "reports", icon: BarChart3, testid: "nav-reports", roles: ["admin", "sales"], module: "reports" },
  { to: "/users", key: "users_roles", icon: UserCog, testid: "nav-users", roles: ["admin"], module: "users" },
  { to: "/settings", key: "settings", icon: SettingsIcon, testid: "nav-settings", roles: ["admin", "finance"], module: "settings" },
];

function LanguageToggle() {
  const { lang, setLang, t } = useI18n();
  return (
    <div className="flex items-center gap-2 px-2">
      <Globe2 className="size-4 text-muted-foreground" aria-hidden />
      <div className="inline-flex items-center border border-border rounded-md overflow-hidden text-xs font-mono">
        <button
          data-testid="lang-toggle-en"
          onClick={() => setLang("en")}
          className={cn(
            "px-2.5 py-1 transition-colors",
            lang === "en" ? "bg-primary text-primary-foreground" : "bg-white text-foreground hover:bg-secondary"
          )}
        >EN</button>
        <button
          data-testid="lang-toggle-tt"
          onClick={() => setLang("tt")}
          className={cn(
            "px-2.5 py-1 transition-colors border-l border-border",
            lang === "tt" ? "bg-primary text-primary-foreground" : "bg-white text-foreground hover:bg-secondary"
          )}
        >TT</button>
      </div>
      <span className="sr-only">{t("language")}</span>
    </div>
  );
}

export default function Layout() {
  const { t } = useI18n();
  const loc = useLocation();
  const { user, logout } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);
  // Sidebar visibility is gated purely by module permission — the base role
  // is now just a label ("manual list wins"). Admin bypasses everything via
  // hasModule() short-circuiting.
  const visible = navItems.filter((n) => hasModule(user, n.module));
  const showClaims = visible.some((n) => n.badge === "claims");
  const { data: claimsSummary } = useQuery({
    queryKey: ["payment-claims", "summary"],
    queryFn: api.paymentClaimsSummary,
    enabled: showClaims,
    refetchInterval: 60_000,
  });
  const badges = { claims: claimsSummary?.pending_count || 0 };
  const { data: leavePending } = useQuery({
    queryKey: ["leave-pending"],
    queryFn: api.leavePendingCount,
    enabled: user?.role === "admin" && visible.some((n) => n.badge === "leave"),
    refetchInterval: 60_000,
  });
  badges.leave = leavePending?.pending_count || 0;
  const { data: billsSum } = useQuery({
    queryKey: ["bills-summary-nav"],
    queryFn: () => api.billsSummary(7),
    enabled: visible.some((n) => n.badge === "bills"),
    refetchInterval: 300_000,
  });
  badges.bills = (billsSum?.overdue_count || 0) + (billsSum?.due_soon_count || 0);

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className="hidden md:flex w-64 flex-col border-r border-border bg-[#FAFAFA] sticky top-0 h-screen"
      >
        <div className="px-5 py-6 border-b border-border flex items-center justify-center">
          <img
            src="/kinos-logo.jpg"
            alt="KINOS — Leading Innovation Technology"
            data-testid="kinos-logo"
            className="h-16 w-auto object-contain"
          />
        </div>

        <nav className="flex-1 py-3 px-2 space-y-0.5">
          {visible.map(({ to, key, icon: Icon, end, testid, badge }) => (
            <NavLink
              key={key}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-all",
                  isActive
                    ? "bg-primary text-primary-foreground font-medium shadow-sm"
                    : "text-foreground hover:bg-secondary"
                )
              }
            >
              <Icon className="size-4" strokeWidth={2} />
              <span>{t(key)}</span>
              {badge && badges[badge] > 0 && (
                <span data-testid={`${testid}-badge`}
                      className={cn("ml-auto inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-[10px] font-bold",
                        badge === "leave" || (badge === "bills" && billsSum?.overdue_count > 0) ? "bg-red-600 text-white" : "bg-amber-400 text-amber-950")}>
                  {badges[badge]}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-border px-3 py-3">
          {user && (
            <div data-testid="user-pill" className="px-2 mb-3">
              <p className="text-xs font-semibold truncate">{user.name}</p>
              <p className="text-[10px] text-muted-foreground mono truncate">{user.email}</p>
              <span className={`inline-block mt-1 px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider uppercase ring-1 ring-inset ${
                user.role === "admin" ? "bg-red-50 text-red-700 ring-red-200" :
                user.role === "sales" ? "bg-amber-50 text-amber-800 ring-amber-200" :
                "bg-emerald-50 text-emerald-700 ring-emerald-200"
              }`}>{user.role}</span>
              <button data-testid="change-password-btn" onClick={() => setPwOpen(true)}
                className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
                <KeyRound className="size-3.5" /> Change password
              </button>
              <button data-testid="logout-btn" onClick={logout}
                className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-red-600">
                <LogOut className="size-3.5" /> Sign out
              </button>
            </div>
          )}
          <LanguageToggle />
          <p className="mt-3 px-2 text-[10px] tracking-wider uppercase text-muted-foreground font-mono">
            v1.0 · Django + React
          </p>
        </div>
      </aside>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />

      {/* Main */}
      <div className="flex-1 min-w-0">
        {/* Mobile header */}
        <header
          data-testid="mobile-header"
          className="md:hidden flex items-center justify-between border-b border-border px-4 py-3 bg-white sticky top-0 z-10"
        >
          <img
            src="/kinos-logo.jpg"
            alt="KINOS"
            className="h-9 w-auto object-contain"
          />
          <LanguageToggle />
        </header>

        {/* Mobile nav pills */}
        <nav className="md:hidden flex gap-1 overflow-x-auto px-3 py-2 border-b border-border bg-white">
          {visible.map(({ to, key, icon: Icon, end, testid }) => (
            <NavLink
              key={key}
              to={to}
              end={end}
              data-testid={`m-${testid}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs whitespace-nowrap border",
                  isActive
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border text-foreground bg-white"
                )
              }
            >
              <Icon className="size-3.5" />
              <span>{t(key)}</span>
            </NavLink>
          ))}
        </nav>

        <main key={loc.pathname} className="p-6 md:p-8 max-w-[1400px] mx-auto">
          <Outlet />
        </main>
      </div>

      <Toaster richColors position="top-right" />
    </div>
  );
}
